import React, { useState, useEffect, useRef } from 'react';
import './Chat.css';
import { getApiBaseUrl } from '../config/api';

const Chat = ({ instances, selectedInstance, onSelectInstance }) => {
  const [contacts, setContacts] = useState([]);
  const [messages, setMessages] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (selectedInstance) {
      fetchContacts();
    }
  }, [selectedInstance]);

  useEffect(() => {
    if (selectedInstance && selectedChat) {
      fetchMessages();
      const interval = setInterval(fetchMessages, 3000);
      return () => clearInterval(interval);
    }
  }, [selectedInstance, selectedChat]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const fetchContacts = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(getApiBaseUrl(`//instances/${selectedInstance.id}/contacts`), {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      const data = await response.json();
      if (data.success) {
        setContacts(data.contacts || []);
      }
    } catch (error) {
      console.error('Failed to fetch contacts:', error);
    }
  };

  const fetchMessages = async () => {
    if (!selectedChat) return;
    
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(getApiBaseUrl(`/instances/${selectedInstance.id}/messages?chat_id=${selectedChat.chat_id}`), {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      const data = await response.json();
      if (data.success) {
        setMessages(data.messages || []);
      }
    } catch (error) {
      console.error('Failed to fetch messages:', error);
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedChat) return;

    setLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(getApiBaseUrl(`/instances/${selectedInstance.id}/send`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          number: selectedChat.chat_id,
          message: newMessage,
        }),
      });

      const data = await response.json();
      if (data.success) {
        setNewMessage('');
        fetchMessages();
      } else {
        alert('Failed to send message: ' + data.error);
      }
    } catch (error) {
      alert('Connection error: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  if (!selectedInstance) {
    return (
      <div className="chat-container">
        <div className="no-instance-selected">
          <div className="no-instance-icon">💬</div>
          <h3>No Device Selected</h3>
          <p>Select a connected WhatsApp device to start chatting</p>
          <div className="available-instances">
            <h4>Available Devices:</h4>
            {instances.filter(i => i.status === 'connected').length === 0 ? (
              <p>No connected devices found</p>
            ) : (
              <div className="instance-list">
                {instances.filter(i => i.status === 'connected').map(instance => (
                  <button
                    key={instance.id}
                    className="instance-btn"
                    onClick={() => onSelectInstance(instance)}
                  >
                    {instance.name}
                    <span className="instance-status">Connected</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="chat-container">
      <div className="chat-sidebar">
        <div className="chat-header">
          <h3>{selectedInstance.name}</h3>
          <span className="instance-phone">{selectedInstance.phone_number}</span>
        </div>
        
        <div className="contacts-list">
          {contacts.length === 0 ? (
            <div className="no-contacts">
              <p>No contacts found</p>
              <small>Send a message to someone to start a conversation</small>
            </div>
          ) : (
            contacts.map(contact => (
              <button
                key={contact.chat_id}
                className={`contact-item ${selectedChat?.chat_id === contact.chat_id ? 'active' : ''}`}
                onClick={() => setSelectedChat(contact)}
              >
                <div className="contact-info">
                  <div className="contact-name">{contact.display_name}</div>
                  <div className="last-message">{contact.last_message}</div>
                </div>
                <div className="contact-meta">
                  <div className="message-time">
                    {new Date(contact.last_message_time).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                  </div>
                  {contact.message_count > 0 && (
                    <div className="message-count">{contact.message_count}</div>
                  )}
                </div>
              </button>
            ))
          )}
        </div>
      </div>

      <div className="chat-main">
        {selectedChat ? (
          <>
            <div className="chat-messages">
              {messages.length === 0 ? (
                <div className="no-messages">
                  <p>No messages yet</p>
                  <small>Start the conversation by sending a message</small>
                </div>
              ) : (
                messages.map(message => (
                  <div
                    key={message.id}
                    className={`message ${message.is_from_me ? 'sent' : 'received'}`}
                  >
                    <div className="message-content">
                      <div className="message-text">{message.content}</div>
                      <div className="message-time">
                        {new Date(message.timestamp).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                      </div>
                    </div>
                  </div>
                ))
              )}
              <div ref={messagesEndRef} />
            </div>

            <form onSubmit={sendMessage} className="message-input">
              <input
                type="text"
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder="Type a message..."
                disabled={loading}
              />
              <button type="submit" disabled={loading || !newMessage.trim()}>
                {loading ? '...' : '➤'}
              </button>
            </form>
          </>
        ) : (
          <div className="no-chat-selected">
            <div className="no-chat-icon">💭</div>
            <h3>Select a conversation</h3>
            <p>Choose a contact from the sidebar to start chatting</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Chat;
